import {ProfileCompletionForm, RecommendationRequest} from '@/profile-creation-wizard/types';
import getters from './getters';
import mutations from './mutations';
import actions from './actions';
import {Description, Education} from '@/profile-creation-wizard/components/for-completion/domain/Profile';
import {Photo} from '@malt/asset-utils';
import {ReasonForJoining, SeniorityLevel} from '@/profile-creation-wizard/components/for-completion/domain/types';
import {MinMaxRuleResource} from '@/common/api/api-raw-types';
import {ActivityArea, Experience} from '@/profile-creation-wizard/components/for-completion/domain/experience/Experience';
import {ProfileCompletionRepositoryAPI} from '@/profile-creation-wizard/components/for-completion/infra/repository/ProfileCompletionRepositoryAPI';
import {ApplicationStateRepositoryAPI} from '@/profile-creation-wizard/components/for-completion/infra/repository/ApplicationStateRepositoryAPI';
import {ExperienceRepositoryAPI} from '@/profile-creation-wizard/components/for-completion/infra/repository/ExperienceRepositoryAPI';
import {pushNotification} from '@malt/joy-entrypoint/src/js/notification';
import {I18nService} from '@malt/vue3-i18n';
import {CreationStepName} from '@/profile-creation-wizard/config/steps/ProfileCreationSteps';
import {ApplicationStateActions} from '@/profile-creation-wizard/components/for-completion/domain/application-state/ApplicationState';
import ProfileCreationRouteHelper from '@/profile-creation-wizard/router/ProfileCreationRouterHelper';

// TODO: We should either inject the domain services or create a new instance of store using parameters which will contain the expected services (ExperienceDomainService)
const profileCompletionRepository = new ProfileCompletionRepositoryAPI();
const experienceRepository = new ExperienceRepositoryAPI();
const applicationStateRepository = new ApplicationStateRepositoryAPI();
const defaultForm: ProfileCompletionForm = {
    description: {content: ''},
    educations: [],
    photo: null,
    linkedinResume: null,
    seniority: null,
    reasons: null,
    // TODO: Create an endpoint for this
    profileDescriptionRule: {min: 200, max: 2000},
    experienceDescriptionRule: {min: 200, max: 2000},
    educationDescriptionRule: {min: 20, max: 2000},
    experiences: [],
    activityAreas: [],
    recommendations: {recoTextToSend: '', recoEmails: ''},
};

export default {
    state: {
        completionForm: defaultForm,
        hasLoadedAllData: false,
        isLoadingData: false,
    },
    mutations: {
        [mutations.setEducations]: (state, educations: Education[]) => {
            state.completionForm.educations = educations;
        },
        [mutations.setProfileDescription]: (state, description: Description) => {
            state.completionForm.description = description;
        },
        [mutations.setPhoto]: (state, photo: Photo) => {
            state.completionForm.photo = photo;
        },
        [mutations.setLinkedinResume]: (state, linkedinResume: File) => {
            state.completionForm.linkedinResume = linkedinResume;
        },
        [mutations.setSeniority]: (state, seniority: SeniorityLevel) => {
            state.completionForm.seniority = seniority;
        },
        [mutations.setReasons]: (state, reasons: ReasonForJoining) => {
            state.completionForm.reasons = reasons;
        },
        [mutations.setProfileDescriptionRule]: (state, descriptionRule: MinMaxRuleResource) => {
            state.completionForm.profileDescriptionRule = descriptionRule;
        },
        [mutations.setExperiences]: (state, experiences: Experience[]) => {
            state.completionForm.experiences = experiences;
        },
        [mutations.setRecommendations]: (state, recommendation: RecommendationRequest) => {
            state.completionForm.recommendations = recommendation;
        },
        [mutations.setActivityAreas]: (state, activityAreas: ActivityArea[]) => {
            state.completionForm.activityAreas = activityAreas;
        },
        [mutations.setHasLoadedAllData]: (state, newValue: boolean) => {
            state.hasLoadedAllData = newValue;
        },
        [mutations.setLoading]: (state, newValue: boolean) => {
            state.isLoadingData = newValue;
        },
    },
    getters: {
        [getters.completionForm]: (state) => state.completionForm,
        [getters.educations]: (state) => state.completionForm.educations,
        [getters.description]: (state) => state.completionForm.description,
        [getters.photo]: (state) => state.completionForm.photo,
        [getters.linkedinResume]: (state) => state.completionForm.linkedinResume,
        [getters.seniority]: (state) => state.completionForm.seniority,
        [getters.reasons]: (state) => state.completionForm.reasons,
        [getters.profileDescriptionRule]: (state) => state.completionForm.profileDescriptionRule,
        [getters.experienceDescriptionRule]: (state) => state.completionForm.experienceDescriptionRule,
        [getters.educationDescriptionRule]: (state) => state.completionForm.educationDescriptionRule,
        [getters.experiences]: (state) => state.completionForm.experiences,
        [getters.recommendations]: (state) => state.completionForm.recommendations,
        [getters.activityAreas]: (state) => state.completionForm.activityAreas,
        [getters.isLoading]: (state) => state.isLoadingData,
        [getters.hasLoadedAllData]: (state) => state.hasLoadedAllData,
    },
    actions: {
        async [actions.fetchProfileDescription](context): Promise<void> {
            const description = await profileCompletionRepository?.fetchDescription();
            context.commit('SET_PROFILE_DESCRIPTION', description);
        },
        async [actions.fetchEducations](context): Promise<void> {
            const educations = await profileCompletionRepository?.fetchEducations();
            context.commit('SET_EDUCATIONS', educations);
        },
        async [actions.fetchExperiences](context): Promise<void> {
            const experiences = await experienceRepository?.fetchExperiences();
            context.commit('SET_EXPERIENCES', experiences);
        },
        async [actions.fetchActivityAreas](context): Promise<void> {
            const activityAreas = await experienceRepository?.fetchActivityAreas();
            context.commit('SET_ACTIVITY_AREAS', activityAreas);
        },
        async [actions.fetchPhoto](context, profileId: string): Promise<void> {
            const photo = await profileCompletionRepository?.fetchPhoto(profileId);
            context.commit('SET_PHOTO', photo);
        },
        async [actions.fetchSeniorityLevel](context): Promise<void> {
            const seniorityLevel = await profileCompletionRepository?.fetchSeniorityLevel();
            context.commit('SET_SENIORITY', seniorityLevel);
        },
        async [actions.fetchProfileDescriptionRule](context): Promise<void> {
            const descriptionRule = await profileCompletionRepository?.fetchDescriptionRule();
            context.commit('SET_PROFILE_DESCRIPTION_RULE', descriptionRule);
        },
        async [actions.fetchCompletionFormData](context): Promise<void> {
            context.commit(mutations.setLoading, true);
            try {
                await Promise.all([
                    context.dispatch(actions.fetchProfileDescription),
                    context.dispatch(actions.fetchEducations),
                    context.dispatch(actions.fetchActivityAreas),
                    context.dispatch(actions.fetchExperiences),
                    context.dispatch(actions.fetchSeniorityLevel),
                ]);
                context.commit(mutations.setHasLoadedAllData, true);
                context.commit(mutations.setLoading, false);
            } catch (e) {
                context.commit(mutations.setLoading, false);
                pushNotification(I18nService.msg('profile.creation.common.notification.error.technical'), {level: 'error'});
            }
        },
        async [actions.updateEducations](context, educations: Education[]): Promise<void> {
            await profileCompletionRepository?.updateEducations(educations);
            await context.dispatch(actions.fetchEducations);
        },
        async [actions.updateExperiences](context, experiences: Experience[]): Promise<void> {
            await experienceRepository?.updateExperiences(experiences);
            await context.dispatch(actions.fetchExperiences);
        },
        async [actions.updateProfileDescription](context, description: string): Promise<void> {
            await profileCompletionRepository.updateDescription(description);
            context.dispatch(actions.fetchProfileDescription);
        },
        async [actions.uploadLinkedinResume](_, file: File): Promise<void> {
            await profileCompletionRepository?.importResumeFromLinkedIn(file);
        },
        async [actions.updateSeniority](context, seniorityLevel: SeniorityLevel | null): Promise<void> {
            await profileCompletionRepository?.updateSeniorityLevel(seniorityLevel);
            context.commit('SET_SENIORITY', seniorityLevel);
        },
        async [actions.updateProfilePreference](_, indexable: boolean): Promise<void> {
            await profileCompletionRepository?.updateProfilePreference(indexable);
        },
        async [actions.updateCurrentStep](_, currentStep: CreationStepName): Promise<void> {
            await profileCompletionRepository?.updateCurrentStep(currentStep);
        },
        async [actions.fetchCurrentStep](_): Promise<string> {
            const currentStep = await profileCompletionRepository?.fetchCurrentStep();
            return currentStep;
        },
        async [actions.confirmApplicationStatus](): Promise<void> {
            try {
                await applicationStateRepository.updateApplicationState(ApplicationStateActions.DONE);
                ProfileCreationRouteHelper.redirectToExternalPath('/profile');
            } catch (e) {
                pushNotification(I18nService.msg('profile.creation.common.notification.error.technical'), {level: 'error'});
            }
        },
    },
};
